import React, { FC, useMemo } from 'react';
import { Box } from '@mui/material';

type Segment = 'front' | 'middle' | 'back';

interface WeightDistributionBarProps {
  containerWeights: number[];
  weightGroup: Segment[];
}

const WeightDistributionBar: FC<WeightDistributionBarProps> = ({ containerWeights, weightGroup }) => {
  const barPercent = useMemo<number>(() => {
    if (!containerWeights.length || !weightGroup.length) {
      return 0;
    }

    const sums: Record<Segment, number> = { front: 0, middle: 0, back: 0 };
    weightGroup.forEach((g, i) => {
      const w = containerWeights[i] || 0;
      if (g in sums) sums[g] += w;
    });

    const total = sums.front + sums.middle + sums.back || 1;
    const diff = sums.front - sums.back;
    const pct = (diff / total) * 100;

    return Math.max(-100, Math.min(100, pct));
  }, [containerWeights, weightGroup]);

  if (!containerWeights.length || !weightGroup.length) {
    return null;
  }

  return (
    <Box position="relative" width="100%" height={16} bgcolor="grey.200" overflow="hidden" pt={1}>
      {barPercent > 0 && (
        <Box
          position="absolute"
          right="50%"
          top={0}
          bottom={0}
          width={`${barPercent}%`}
          bgcolor="primary.main"
          sx={{ zIndex: 0 }}
        />
      )}
      {barPercent < 0 && (
        <Box
          position="absolute"
          left="50%"
          top={0}
          bottom={0}
          width={`${-barPercent}%`}
          bgcolor="primary.main"
          sx={{ zIndex: 0 }}
        />
      )}
      <Box
        position="absolute"
        left="50%"
        top={0}
        bottom={0}
        width={2}
        bgcolor="text.primary"
        sx={{ zIndex: 1 }}
      />
    </Box>
  );
};

export default WeightDistributionBar;